import { useEffect, useState } from "react";
import { useAuth } from "../../../shared/context/AuthContext";
import userapi from "../../../services/userapi";
import { readLastLoginUsername } from "../../../utils/lastLoginIdentity";

export default function useLoginBoxLogic({ onLoginSuccess }) {
  const { login, loginError: contextLoginError, updateAuthToken } = useAuth();

  const [formData, setFormData] = useState({
    username: readLastLoginUsername(),
    password: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);

  useEffect(() => {
    if (!contextLoginError) return;
    if (contextLoginError === "CREDENTIALS_EXPIRED") {
      setError(
        "You need to change your password for your first login. Password must be at least 6 characters.",
      );
      setSuccessMessage("");
      setPasswordExpired(true);
      return;
    }
    setError(contextLoginError);
    setSuccessMessage("");
  }, [contextLoginError]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError("");
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (passwordExpired) {
      if (formData.newPassword !== formData.confirmPassword) {
        setError("New passwords do not match.");
        return;
      }
      if (formData.newPassword.length < 6) {
        setError("New password must be at least 6 characters.");
        return;
      }

      try {
        const changeResponse = await userapi.login(
          formData.username,
          formData.password,
          formData.newPassword,
        );
        if (changeResponse && changeResponse.jwtToken) {
          updateAuthToken(changeResponse.jwtToken);
          setSuccessMessage(
            "Your password has been changed successfully. Please log in with your new password.",
          );
          setError("");
          setPasswordExpired(false);
          setFormData((prev) => ({
            username: prev.username,
            password: "",
            newPassword: "",
            confirmPassword: "",
          }));
        } else {
          setSuccessMessage("");
          setError(
            "Password change failed. Please try again or contact an administrator.",
          );
        }
      } catch (apiError) {
        setSuccessMessage("");
        setError(
          apiError.message || "An error occurred while changing the password.",
        );
      }
      return;
    }

    try {
      const loggedIn = await login(formData.username, formData.password);
      if (!loggedIn) return;
      if (typeof onLoginSuccess === "function") {
        onLoginSuccess();
      }
    } catch (err) {
      if (err.message === "CREDENTIALS_EXPIRED") {
        setPasswordExpired(true);
        setError(
          "You need to change your password for your first login. Password must be at least 6 characters.",
        );
      } else {
        setError(err.message || "An error occurred during login.");
      }
    }
  };

  return {
    formData,
    error,
    successMessage,
    showPassword,
    passwordExpired,
    setShowPassword,
    handleInputChange,
    handleSubmit,
  };
}
